import React, { useState } from 'react';
import { Camera, Lightbulb, Download, Layers, ZoomIn, Wrench, Hand, ScanLine, Ruler, Grid3x3, Crosshair } from 'lucide-react';
import useEditorStore from '../store/useEditorStore';
import { ORIENTATIONS } from '../mock/mock';
import { toast } from 'sonner';
import { exportProjectPDF, exportWorkshopPDF } from '../services/pdfExport';
import { exportPosePlanA4, exportPosePlanTrue1to1 } from '../services/posePlanExport';
import FixationHolesEditor from './FixationHolesEditor';

const ZOOM_PRESETS = [1, 10, 25, 50, 100];

export default function BottomToolbar() {
  const orientation = useEditorStore((s) => s.orientation);
  const setOrientation = useEditorStore((s) => s.setOrientation);
  const segments = useEditorStore((s) => s.segments);
  const exploded = useEditorStore((s) => s.exploded);
  const toggleExploded = useEditorStore((s) => s.toggleExploded);
  const ledOn = useEditorStore((s) => s.ledOn);
  const toggleLed = useEditorStore((s) => s.toggleLed);
  const setZoomRequest = useEditorStore((s) => s.setZoomRequest);
  const viewScale = useEditorStore((s) => s.viewScale);
  const panMode = useEditorStore((s) => s.panMode);
  const togglePanMode = useEditorStore((s) => s.togglePanMode);
  const planMode = useEditorStore((s) => s.planMode);
  const togglePlanMode = useEditorStore((s) => s.togglePlanMode);

  const [holesEditorOpen, setHolesEditorOpen] = useState(false);

  const partsCount = segments.length * 3;

  const orientMap = {
    F: 'front', T: 'top', I: 'iso', D: 'back', S: 'right', B: 'bottom', L: 'free',
  };

  const orientLabelFr = {
    front: 'Avant', back: 'Arrière', top: 'Dessus', bottom: 'Dessous',
    left: 'Gauche', right: 'Droite', iso: 'Iso', free: 'Libre',
  };

  return (
    <>
      <div className="zoom-preset-bar" data-testid="zoom-preset-bar">
        <span className="zoom-preset-label"><ZoomIn size={12} /> Zoom</span>
        {ZOOM_PRESETS.map((pct) => {
          // Determine if this preset is approximately current
          const cssPxPerMmScreen = 3.78;
          const currentPct = ((viewScale.pxPerMm || 0) / cssPxPerMmScreen) * 100;
          const active = Math.abs(currentPct - pct) / pct < 0.08;
          return (
            <button
              key={pct}
              className={`zoom-preset-btn ${active ? 'active' : ''}`}
              onClick={() => {
                setZoomRequest(pct);
                toast.success(`Zoom ${pct}% (taille réelle)`);
              }}
              data-testid={`zoom-preset-${pct}`}
            >
              {pct}%
            </button>
          );
        })}
      </div>

      <div className="orient-toolbar">
        <span className="orient-label">Vue :</span>
        <span style={{ color: 'var(--accent)', fontSize: 12, fontWeight: 600, padding: '0 8px' }}>
          {orientLabelFr[orientation] || 'Libre'}
        </span>
        {ORIENTATIONS.map((o) => {
          const key = orientMap[o.id];
          return (
            <button
              key={o.id}
              className={orientation === key ? 'active' : ''}
              onClick={() => setOrientation(key)}
              title={o.label}
            >
              {o.id}
            </button>
          );
        })}
      </div>

      <div className="canvas-corner-icons">
        <button
          className={`corner-btn ${panMode ? 'active' : ''}`}
          title={panMode ? 'Mode déplacer (main) — clic gauche = déplacer' : 'Activer le mode main pour déplacer la vue'}
          onClick={() => { togglePanMode(); toast.success(panMode ? 'Mode rotation' : 'Mode déplacer (main) activé'); }}
          data-testid="btn-pan-mode"
        >
          <Hand size={16} />
        </button>
        <button
          className={`corner-btn ${exploded ? 'active' : ''}`}
          title={exploded ? 'Vue assemblée' : 'Vue éclatée — voir les vis et guides de perçage'}
          onClick={() => { toggleExploded(); toast.success(exploded ? 'Vue assemblée' : 'Vue éclatée — vis et guides visibles'); }}
          data-testid="btn-exploded-view"
        >
          <Layers size={16} />
        </button>
        <button
          className={`corner-btn ${ledOn ? 'active' : ''}`}
          title={ledOn ? 'Éteindre LED' : 'Allumer LED'}
          onClick={() => { toggleLed(); toast.success(ledOn ? 'LED éteint' : 'LED allumé'); }}
        >
          <Lightbulb size={16} />
        </button>
        <button className="corner-btn" title="Capture d'écran" onClick={() => toast.success('Capture enregistrée')}>
          <Camera size={16} />
        </button>
        <button
          className={`corner-btn ${planMode ? 'active' : ''}`}
          title={planMode ? 'Quitter le mode plan de pose' : 'Mode Plan de pose (fond blanc + éclatement + tracés noirs)'}
          onClick={() => { togglePlanMode(); toast.success(planMode ? 'Vue normale' : 'Mode Plan de pose activé'); }}
          data-testid="btn-plan-mode"
        >
          <ScanLine size={16} />
        </button>
        <button
          className="corner-btn"
          title="Plan de pose atelier (aperçu + repères)"
          onClick={async () => {
            try {
              const state = useEditorStore.getState();
              toast.info('Génération de la fiche atelier...');
              await new Promise((r) => setTimeout(r, 100));
              const fn = await exportWorkshopPDF(state);
              toast.success(`Fiche atelier générée : ${fn}`);
            } catch (e) {
              console.error(e);
              toast.error('Erreur : ' + (e.message || e));
            }
          }}
          data-testid="btn-workshop-plan"
        >
          <Wrench size={16} />
        </button>
        <button
          className="corner-btn"
          title="Placer manuellement les trous de fixation (vue arrière)"
          onClick={() => setHolesEditorOpen(true)}
          data-testid="btn-fix-holes-editor"
        >
          <Crosshair size={16} />
        </button>
        <button
          className="corner-btn"
          title="Plan de pose scaled (contours + repères de perçage)"
          onClick={async () => {
            try {
              const state = useEditorStore.getState();
              toast.info('Génération du plan de pose...');
              await new Promise((r) => setTimeout(r, 100));
              const r = await exportPosePlanA4(state);
              toast.success(`Plan ${r.format} · Échelle 1:${r.ratio} (${r.totalWidthMm}×${r.heightMm} mm) : ${r.filename}`);
            } catch (e) {
              console.error(e);
              toast.error('Erreur : ' + (e.message || e));
            }
          }}
          data-testid="btn-pose-plan"
        >
          <Ruler size={16} />
        </button>
        <button
          className="corner-btn"
          title="Plan de pose 1:1 tuilé (multi-pages A4 avec marques d'assemblage)"
          onClick={async () => {
            try {
              const state = useEditorStore.getState();
              toast.info('Génération du gabarit 1:1 tuilé...');
              await new Promise((r) => setTimeout(r, 100));
              const r = await exportPosePlanTrue1to1(state);
              toast.success(`Gabarit 1:1 · ${r.cols}×${r.rows} pages A4 (${r.pages}) : ${r.filename}`);
            } catch (e) {
              console.error(e);
              toast.error('Erreur : ' + (e.message || e));
            }
          }}
          data-testid="btn-pose-plan-1to1"
        >
          <Grid3x3 size={16} />
        </button>
      </div>

      <div className="status-pill">
        <span className="pill-dot" />
        <span>{partsCount} pièce(s) générée(s) avec succès.</span>
        <span className="free-tag">(L5 - MODÈLE GRATUIT)</span>
        <button
          className="btn-export"
          onClick={async () => {
            try {
              toast.info('Génération du cahier des charges PDF...');
              await new Promise((r) => setTimeout(r, 100));
              const state = useEditorStore.getState();
              const filename = await exportProjectPDF(state);
              toast.success(`PDF généré : ${filename}`);
            } catch (e) {
              console.error(e);
              toast.error('Erreur PDF : ' + (e?.message || 'inconnue'));
            }
          }}
        >
          <Download size={14} /> Exporter le projet
        </button>
      </div>
      <FixationHolesEditor open={holesEditorOpen} onClose={() => setHolesEditorOpen(false)} />
    </>
  );
}
